import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import AdvancedSelect from './AdvancedSelect';
import { RECIPIENT_FILTER_ATTRIBUTES } from '../../../../../../../consts/recipientFilter';
import { useRecipientFilter } from '../../../../../../../contexts/RecipientFilter';
import { IconButton } from '@livingdesign/react';
import { TrashCan } from '@livingdesign/icons';

export const QA_ID_PREFIX = "RFCriteria";

function RFCriteria({
    attribute = null,
    values = [],
    index,
}) {

    const {
        updateCriteria,
        getIndexOfLastCondition,
        setAlertMessage,
        fetchAttributeFilterValues,
        generateRawFilter,
        parsedFilter,
        deleteCondition,
        generateRawFilterForGroup,
        getAllTheAttributesUsedInGroup,
        isValidUpto,
    } = useRecipientFilter();

    const [recipientFilterAttributesOptions, setRecipientFilterAttributesOptions] = useState(RECIPIENT_FILTER_ATTRIBUTES);

    const [attributeValues, setAttributeValues] = useState(values.map(value => ({ label: value, value })));
    const [attributeValuesLoading, setAttributeValuesLoading] = useState(false);
    const [isServerSideSearch, setIsServerSideSearch] = useState(false);

    const [selectedAttribute, setSelectedAttribute] = useState(attribute);
    const [selectedValue, setSelectedValue] = useState(values.map(value => ({ label: value, value })));

    useEffect(() => {
        const usedAttributes = getAllTheAttributesUsedInGroup(index) || [];
        
        const availableAttributes = RECIPIENT_FILTER_ATTRIBUTES.filter(
            attr => !usedAttributes.includes(attr.value)
        );
        setRecipientFilterAttributesOptions(availableAttributes);

        return () => {
            // Cleanup state on unmount
            setRecipientFilterAttributesOptions(RECIPIENT_FILTER_ATTRIBUTES);
        };
    }, [parsedFilter]);

    useEffect(() => {
        setSelectedAttribute(attribute);
        // Get the attribute datatype
        const attributeObj = RECIPIENT_FILTER_ATTRIBUTES.find(attr => attr.value === attribute);
        const attributeDataType = attributeObj ? attributeObj.DataType : undefined;
        if(attributeDataType === "boolean") {
            let label = "";
            let value = "";
            if(values.length > 0) {
                label = values[0] === "Y" ? "True" : "False";
                value = values[0];
            }
            setSelectedValue({ label, value });
        } else {
            setSelectedValue(values.map(value => ({ label: value, value })));
        }
        updateCriteria(index, attribute, values);
    }, [attribute, values]);

    const updateAttributeValues = async (selectedAttribute) => {
        const abortController = new AbortController();

        // If the selected attribute is "DagNm", set a fixed value and disable server-side search
        if(selectedAttribute === "DagNm") {
            setAttributeValues([{ label: "US", value: "US" }]);
            setIsServerSideSearch(false);
            return;
        }
        if (!selectedAttribute) {
            setAttributeValues([]);
            setIsServerSideSearch(false);
            return;
        }

        setAttributeValuesLoading(true);
        try {
            console.log(`parsedFilter.slice(0, ${index} - 1)=`, parsedFilter.slice(0, index - 1));
            
            const filter = (index === 0) ? "" : generateRawFilterForGroup(index);
            const field = selectedAttribute;

            const response = await fetchAttributeFilterValues(filter, field);
            
            if (response) {
                const { values, count } = response;
                
                // Convert values to options format
                const options = values.map(value => ({
                    label: value,
                    value: value
                }));
                
                setAttributeValues(options);
                
                // Enable server-side search if count > 10000
                setIsServerSideSearch(count > 10000);
            }
        } catch (error) {
            console.error('Error updating attribute values:', error);
            setAttributeValues([]);
            setIsServerSideSearch(false);
        } finally {
            setAttributeValuesLoading(false);
        }

        return () => {
            abortController.abort();
        };
    };

    const handleServerSideValueSearch = useCallback(async (searchTerm) => {
        const abortController = new AbortController();
        if (!selectedAttribute) return;
        
        setAttributeValuesLoading(true);
        try {
            const filter = (index === 0) ? "" : generateRawFilterForGroup(index);
            const field = selectedAttribute;
            
            // If search term is empty, fetch all original data
            if (!searchTerm.trim()) {
                const response = await fetchAttributeFilterValues(filter, field);
                if (response) {
                    const { values } = response;
                    const options = values.map(value => ({
                        label: value,
                        value: value
                    }));
                    setAttributeValues(options);
                }
            } else {
                // Fetch search results
                const response = await fetchAttributeFilterValues(filter, field, searchTerm);
                if (response) {
                    const { values } = response;
                    const options = values
                        .filter(value => value.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map(value => ({
                            label: value,
                            value: value
                        }));
                    setAttributeValues(options);
                }
            }
        } catch (error) {
            console.error('Error in server-side search:', error);
        } finally {
            setAttributeValuesLoading(false);
        }

        return () => {
            abortController.abort();
        };
    }, [selectedAttribute, index, generateRawFilter, parsedFilter, fetchAttributeFilterValues]);

    const getSelectedAttribute = useMemo(() => {
        console.log(RECIPIENT_FILTER_ATTRIBUTES.find(attr => attr.value === selectedAttribute));
        return RECIPIENT_FILTER_ATTRIBUTES.find(attr => attr.value === selectedAttribute);
    }, [selectedAttribute]);

    const attributeDataType = useMemo(() => {
        const attr = RECIPIENT_FILTER_ATTRIBUTES.find(attr => attr.value === selectedAttribute);
        return attr?.DataType || null;
    }, [selectedAttribute]);

    const renderAttributeValueSelector = useCallback((attributeDataType) => {
        const props = {}
        if(attributeDataType === "Array<String>") {
            props.isMulti = true;
            props.searchable = true;
            props.options = attributeValues;
            props.value = selectedValue || null;
            props.loading = attributeValuesLoading;
            props.isServerSideSearch = isServerSideSearch;
            props.onSearch = isServerSideSearch ? handleServerSideValueSearch : null;
            props.onChange = (values = []) => {
                const lastConditionIndex = getIndexOfLastCondition();
                if(index < lastConditionIndex) {
                    setAlertMessage({
                        show: true,
                        title: "Invalid Operation",
                        message: "You can only modify the last condition in the filter sequence.",
                        cancelButtonText: "Close",
                        confirmButtonText: "Okay",
                        handleConfirm: () => setAlertMessage({ show: false })
                    });
                    return;
                }
                setSelectedValue(values);
                let _values = values.map(option => option.value);
                updateCriteria(index, selectedAttribute, _values);
            };
            props.placeholder = "Select a value...";
            props.disabled = !selectedAttribute;
        } else if (attributeDataType === "boolean") {
            props.isMulti = false;
            props.searchable = false;
            props.options = [
                { label: "True", value: "Y" },
                { label: "False", value: "N" }
            ];
            props.value = selectedValue || null;
            props.onChange = (option) => {
                const lastConditionIndex = getIndexOfLastCondition();
                if(index < lastConditionIndex) {
                    setAlertMessage({
                        show: true,
                        title: "Invalid Operation",
                        message: "You can only modify the last condition in the filter sequence.",
                        cancelButtonText: "Close",
                        confirmButtonText: "Okay",
                        handleConfirm: () => setAlertMessage({ show: false })
                    });
                    return;
                }
                setSelectedValue(option);
                let _values = [];
                _values.push(option.value);
                updateCriteria(index, selectedAttribute, _values);
            };
            props.placeholder = "Select a value...";
            props.disabled = !selectedAttribute;
        } else {
            props.disabled = true;
            props.options = [];
            props.value = null;
            props.isMulti = false;
            props.searchable = false;
            props.placeholder = "Select a value...";
        }
        return (
            <AdvancedSelect
                {...props}
                data-testid={`${QA_ID_PREFIX}-value-select`}
            />
        )
    }, [attributeValues, attributeValuesLoading, getIndexOfLastCondition, handleServerSideValueSearch, index, isServerSideSearch, selectedAttribute, selectedValue, setAlertMessage, setSelectedValue]);

  return (
    <div className='rf-criteria' data-testid={`${QA_ID_PREFIX}-container`}>
        <div className='rf-criteria-attribute' data-testid={`${QA_ID_PREFIX}-attribute-section`}>
            <h3 data-testid={`${QA_ID_PREFIX}-attribute-title`}>Attribute</h3>
            <div className='rf-criteria-attribute-select' data-testid={`${QA_ID_PREFIX}-attribute-select-container`}>
                <AdvancedSelect
                    options={recipientFilterAttributesOptions}
                    isMulti={false}
                    searchable={true}
                    value={getSelectedAttribute}
                    onChange={(option) => {
                        const lastConditionIndex = getIndexOfLastCondition();
                        if(index < lastConditionIndex) {
                            setAlertMessage({
                                show: true,
                                title: "Invalid Operation",
                                message: "You can only modify the last condition in the filter sequence.",
                                cancelButtonText: "Close",
                                confirmButtonText: "Okay",
                                handleConfirm: () => {
                                    setAlertMessage({ show: false })
                                }
                            });
                            return;
                        }
                        setSelectedAttribute(option?.value);
                        setSelectedValue(null);
                        updateCriteria(index, option?.value, []);
                        updateAttributeValues(option?.value);
                    }}
                    placeholder='Select an attribute...'
                    disabled={!isValidUpto(index - 1)}
                    data-testid={`${QA_ID_PREFIX}-attribute-select`}
                />
            </div>
        </div>
        <div className='rf-criteria-value' data-testid={`${QA_ID_PREFIX}-value-section`}>
            <h3 data-testid={`${QA_ID_PREFIX}-value-title`}>Value</h3>
            <div className='rf-criteria-value-select' data-testid={`${QA_ID_PREFIX}-value-select-container`}>
            {
                renderAttributeValueSelector(attributeDataType)
            }
            </div>
        </div>
        <div data-testid={`${QA_ID_PREFIX}-delete-section`}>
            {index !== 0 && 
                <IconButton
                    onClick={() => {
                        setAlertMessage({
                            show: true,
                            title: "Warning",
                            message: "Deleting this condition will remove all the conditions below this as well.",
                            cancelButtonText: "Close",
                            confirmButtonText: "Okay",
                            handleConfirm: () => {
                                deleteCondition(index);
                                setAlertMessage({ show: false })
                            }
                        });
                    }}
                    a11yLabel='Remove condition'
                    data-testid={`${QA_ID_PREFIX}-delete-button`}
                >
                    <TrashCan size="small" data-testid={`${QA_ID_PREFIX}-delete-icon`} />
                </IconButton>
            }
        </div>
    </div>
  )
}

export default RFCriteria;